import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, Linkedin, Instagram } from "lucide-react";
import { NAV_LINKS, CATEGORIES, CONTACT, BRANCHES, ASSETS, SOCIAL } from "../mock";
import { Container, WhatsappIcon, Honeypot, ExportBadge } from "./ui-kit";
import { postNewsletter } from "../api";
import { toast } from "sonner";
import { useState } from "react";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [iys, setIys] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Lütfen geçerli bir e-posta adresi girin.");
      return;
    }
    if (!iys) {
      toast.error("Devam etmek için ticari elektronik ileti onayını işaretleyin.");
      return;
    }
    try {
      await postNewsletter({ email, website: hp });
      setEmail("");
      setIys(false);
      toast.success("Haber listemize üye oldunuz. Teşekkürler!");
    } catch (err) {
      toast.error(err.message || "Kayıt başarısız oldu, lütfen tekrar deneyin.");
    }
  };

  return (
    <footer className="bg-sand border-t border-line" data-testid="footer">
      {/* Bülten */}
      <div className="border-b border-line">
        <Container className="py-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-brand">Bülten</p>
            <h3 className="font-display font-light text-2xl sm:text-3xl text-ink mt-3">Yeniliklerden ilk siz haberdar olun</h3>
            <p className="text-ink2 text-sm mt-3 max-w-md leading-relaxed">
              Yeni ürün grupları, renk kartelası güncellemeleri ve katalog duyuruları için e-posta listemize katılın.
            </p>
          </div>
          <form onSubmit={subscribe} noValidate className="w-full max-w-md lg:justify-self-end" data-testid="newsletter-form">
            <div className="flex w-full">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="E-posta adresiniz"
                data-testid="newsletter-email-input"
                className="flex-1 min-w-0 h-12 px-5 rounded-l-full bg-white border border-line border-r-0 text-sm text-ink placeholder:text-mute focus:outline-none focus:border-ink transition-colors"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-button"
                className="h-12 px-6 rounded-r-full bg-ink hover:bg-brand text-white text-[12px] font-semibold tracking-[0.12em] uppercase transition-colors inline-flex items-center gap-2"
              >
                Abone Ol <ArrowRight size={14} />
              </button>
            </div>
            <label className="flex items-start gap-2.5 mt-3 cursor-pointer" data-testid="newsletter-consent">
              <input
                type="checkbox"
                checked={iys}
                onChange={(e) => setIys(e.target.checked)}
                data-testid="newsletter-consent-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#D9261C] shrink-0"
              />
              <span className="text-[11.5px] text-ink2 leading-relaxed">
                Tarafıma ticari elektronik ileti gönderilmesini ve bu amaçla kişisel verilerimin işlenmesini kabul ediyorum.{" "}
                <Link to="/kvkk-aydinlatma-metni" className="text-ink hover:text-brand transition-colors underline">Aydınlatma Metni</Link>
              </span>
            </label>
            <Honeypot value={hp} onChange={(e) => setHp(e.target.value)} />
          </form>
        </Container>
      </div>

      {/* Ana footer */}
      <Container className="py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <img src={ASSETS.logoFull} alt="Koşan Düğme" className="h-10 w-auto" />
          <p className="text-ink2 text-sm leading-relaxed mt-6 max-w-sm">
            1993 yılından bu yana metal tekstil aksesuarlarında; çıtçıt, kuşgözü, perçin, kot düğme, toka, stoper ve bağ ucu üretiminde güvenilir çözüm ortağınız.
          </p>
          <div className="mt-8">
            <ExportBadge />
          </div>
          <div className="flex items-center gap-3 mt-8" data-testid="footer-social">
            <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="social-linkedin" className="w-10 h-10 rounded-full border border-line flex items-center justify-center text-ink2 hover:text-white hover:bg-[#0A66C2] hover:border-[#0A66C2] transition-colors">
              <Linkedin size={16} />
            </a>
            <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram" className="w-10 h-10 rounded-full border border-line flex items-center justify-center text-ink2 hover:text-white hover:bg-[#E1306C] hover:border-[#E1306C] transition-colors">
              <Instagram size={16} />
            </a>
            <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid="social-whatsapp" className="w-10 h-10 rounded-full border border-line flex items-center justify-center text-ink2 hover:text-white hover:bg-[#25D366] hover:border-[#25D366] transition-colors">
              <WhatsappIcon size={15} />
            </a>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-[11px] font-semibold tracking-[0.25em] uppercase text-mute mb-6">Sayfalar</h4>
          <ul className="space-y-3.5">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-ink hover:text-brand transition-colors" data-testid={`footer-link-${l.to === "/" ? "home" : l.to.slice(1)}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-[11px] font-semibold tracking-[0.25em] uppercase text-mute mb-6">Ürün Grupları</h4>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-3.5">
            {CATEGORIES.map((c) => (
              <li key={c.id}>
                <Link to={`/urunler?kategori=${c.id}`} className="text-[13px] text-ink hover:text-brand transition-colors capitalize">
                  {c.name.toLocaleLowerCase("tr")}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-[11px] font-semibold tracking-[0.25em] uppercase text-mute mb-6">İletişim</h4>
          <ul className="space-y-5 text-sm">
            {BRANCHES.map((b) => (
              <li key={b.id} className="flex gap-3">
                <MapPin size={15} className="text-brand shrink-0 mt-0.5" />
                <div>
                  <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-mute">{b.name}</p>
                  <p className="text-ink leading-relaxed mt-1">{b.address}</p>
                </div>
              </li>
            ))}
            <li className="flex gap-3">
              <Phone size={15} className="text-brand shrink-0 mt-0.5" />
              <a href={CONTACT.phoneHref} className="text-ink hover:text-brand transition-colors">{CONTACT.phone}</a>
            </li>
            <li className="flex gap-3">
              <WhatsappIcon size={15} className="text-brand shrink-0 mt-0.5" />
              <span className="flex flex-col gap-1">
                <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" className="text-ink hover:text-brand transition-colors" data-testid="footer-whatsapp">
                  WhatsApp Sipariş · {CONTACT.whatsapp}
                </a>
                <a href={CONTACT.whatsappHref2} target="_blank" rel="noopener noreferrer" className="text-ink hover:text-brand transition-colors" data-testid="footer-whatsapp-2">
                  WhatsApp Sipariş · {CONTACT.whatsapp2}
                </a>
              </span>
            </li>
            <li className="flex gap-3">
              <Mail size={15} className="text-brand shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                {CONTACT.emails.map((m) => (
                  <a key={m} href={`mailto:${m}`} className="text-ink hover:text-brand transition-colors">{m}</a>
                ))}
              </div>
            </li>
          </ul>
        </div>
      </Container>

      <div className="border-t border-line">
        <Container className="py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-mute">
          <p>© {new Date().getFullYear()} Koşan Düğme İmalat San. ve Tic. Ltd. Şti. Tüm hakları saklıdır.</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 justify-center">
            <Link to="/gizlilik" className="hover:text-brand transition-colors" data-testid="footer-privacy-link">
              Gizlilik Politikası
            </Link>
            <Link to="/kvkk-aydinlatma-metni" className="hover:text-brand transition-colors" data-testid="footer-kvkk-link">
              KVKK Aydınlatma Metni
            </Link>
            <Link to="/cerez-politikasi" className="hover:text-brand transition-colors" data-testid="footer-cookies-link">
              Çerez Politikası
            </Link>
            <span className="tracking-[0.2em] uppercase hidden sm:inline">İhracata Uygun Ürün Garantisi</span>
          </div>
        </Container>
      </div>
    </footer>
  );
};

export default Footer;
